import { Component, ComponentChildren } from 'preact'
import classNames from 'classnames'

import PaginationComponent from '../../control-components/ui-components/PaginationComponent'
import { subscribe } from '../../utils/globals'
import Checkbox from '../../control-components/ui-components/Checkbox'
import Tooltip from '../../control-components/ui-components/Tooltip'

import TextButton from '../../control-components/ui-components/TextButton'
import Icon from '../assets'

import { Model, TableRow, Sort } from './types'
import Sorting from './Sorting'
import Row from './Row'

export const defaultCellSize = 'm'

interface Props {
  model?: Model[]
  rows: TableRow[]
  rowId?: string
  sort?: Sort
  onSortChange?: (order: string) => void
  narrow?: boolean
  classes?: string[]
  style?: { [key: string]: any }
  withPagination?: {}
  actionElements?: JSX.Element
  firstColumnSticky?: boolean
  staticWidth?: boolean
  withMultiSelect?: boolean
  onSelectRow?: (rowId: number, checked: boolean) => void
  onSelectAll?: () => void
  onClearSelection?: () => void
  selectedRows?: number[]
  allSelected?: boolean
  entity?: string
  bulkActions?: ComponentChildren
  isActionBarVisible?: boolean
}

interface State {
  isMenuOpen: boolean
}

class Table extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      isMenuOpen: true
    }
  }

  componentDidMount() {
    subscribe('control_sidebar_state', (payload) => this.setState({ isMenuOpen: payload.isOpen }))
  }

  shouldShowActionBar = (): boolean => {
    const {
      withPagination,
      actionElements,
      selectedRows,
      isActionBarVisible = true
    } = this.props
    if (!isActionBarVisible) {
      return false
    }

    if (selectedRows && selectedRows.length > 0) {
      return true
    }

    return !!(withPagination ?? actionElements)
  }

  render() {
    const {
      model,
      rows,
      rowId,
      sort,
      onSortChange,
      narrow,
      classes = [],
      style = {},
      withPagination,
      actionElements,
      firstColumnSticky = false,
      staticWidth = false,
      withMultiSelect,
      selectedRows,
      allSelected,
      onSelectRow,
      onSelectAll,
      onClearSelection,
      entity,
      bulkActions
    } = this.props
    const {
      isMenuOpen
    } = this.state
    const useObjectModel = model && Object.prototype.hasOwnProperty.call(model[0], 'header')
    const showActionBar = this.shouldShowActionBar()

    return (
      <div className='control-table'>
        {showActionBar && <div className='action-bar-wrapper'>
          {selectedRows && selectedRows.length > 0
            ? <div className='action-bar'>
              <div className='control-table__selected'>
                <div
                  className='control-table__selected-count'
                >{selectedRows.length} {selectedRows.length > 1 ? entity : entity?.slice(0, -1)} selected
                </div>
                <TextButton className='control-table__clear-selection' onClick={onClearSelection}>Clear
                  selection</TextButton>
              </div>
              {bulkActions && bulkActions}
            </div>
            : <div className='action-bar'>
              {actionElements && actionElements}
              {withPagination && <PaginationComponent {...withPagination} />}
            </div>}
        </div>}
        <div
          className={classNames('table-wrapper', { 'with-action-bar': showActionBar })}
          style={staticWidth ? 'transition: none' : { width: `calc(100vw - ${isMenuOpen ? '376' : '152'}px)` }}
        >
          <table
            className={classNames({
              narrow: narrow
            }, ...classes)} style={style}
          >
            <thead>
              <tr>
                {withMultiSelect && <th className='fixed-column-header table-header--multiselect'>
                  <Checkbox
                    qaClasses='control-table__select_all'
                    onUpdate={() => onSelectAll?.()}
                    checked={!!allSelected}
                  />
                </th>}
                {
                  model
                    ? Object.keys(model).map((key: string) => {
                      if (model[Number(key)].isHidden) {
                        return null
                      }
                      return (
                        <th key={key} className={`${model[Number(key)].size ?? defaultCellSize} ${firstColumnSticky && key === '0' ? 'fixed-column-header' : ''}`}>
                          {!Object.prototype.hasOwnProperty.call(model[Number(key)], 'sorting') &&
                            <div className={`table-header-cell-${model[Number(key)].accessor}`}>
                              {useObjectModel ? model[Number(key)].header : model[Number(key)]}
                              {Object.prototype.hasOwnProperty.call(model[Number(key)], 'tooltipMessage') &&
                                <Tooltip
                                  trigger={<Icon icon='question' width={16} height={16} />}
                                  tooltipMessage={`${model[Number(key)].tooltipMessage}`}
                                  withArrow
                                  position='center'
                                  noWrap
                                  top={24}
                                />}
                            </div>}
                          {Object.prototype.hasOwnProperty.call(model[Number(key)], 'sorting') &&
                            <div
                              className={`table-header-cell-${model[Number(key)].accessor} sortable`}
                              onClick={() =>
                                onSortChange?.(model[Number(key)].sorting ?? '')}
                            >
                              {useObjectModel ? model[Number(key)].header : model[Number(key)]}
                              {Object.prototype.hasOwnProperty.call(model[Number(key)], 'tooltipMessage') &&
                                <Tooltip
                                  trigger={<Icon icon='question' width={16} height={16} />}
                                  tooltipMessage={`${model[Number(key)].tooltipMessage}`}
                                  withArrow
                                  position='center'
                                  noWrap
                                  top={24}
                                />}
                              <Sorting
                                direction={sort?.direction}
                                isEnabled={sort?.order === model[Number(key)].sorting}
                              />
                            </div>}
                        </th>
                      )
                    })
                    : rows[0].map((key: Model) =>
                      <th
                        key={key.accessor}
                        className={`${key.size ?? defaultCellSize} ${firstColumnSticky && key.size === '0' ? 'fixed-column-header' : ''}`}
                      >
                        <div className={`table-header-cell-${key.accessor} `}>{key.header}</div>
                      </th>
                    )
                }
              </tr>
            </thead>
            <tbody>
              {
                Object.keys(rows).map((key: string) => {
                  return (
                    <Row
                      // @ts-expect-error
                      key={key}
                      displayFirstColumnBold={firstColumnSticky}
                      id={rowId}
                      cells={rows[Number(key)]}
                      model={useObjectModel ? model : null}
                      isSelectable={withMultiSelect}
                      isSelected={selectedRows && !!rowId && selectedRows.includes(rows[Number(key)][rowId])}
                      onSelectRow={onSelectRow}
                    />
                  )
                })
              }
            </tbody>
          </table>
        </div>
      </div>
    )
  }
}

export default Table
